import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import LinkIcon from "@material-ui/icons/Link";
import PhoneIcon from "@material-ui/icons/Phone";
import ReplyIcon from "@material-ui/icons/Reply";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";

import { extractVarIndices } from "./templatePayload";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: "#E5DDD5",
    backgroundImage:
      "linear-gradient(rgba(229, 221, 213, 0.92), rgba(229, 221, 213, 0.92))",
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    minHeight: 380,
    height: "100%",
  },
  bubble: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    maxWidth: 320,
    boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    marginLeft: "auto",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      right: -8,
      width: 0,
      height: 0,
      borderStyle: "solid",
      borderWidth: "0 8px 8px 0",
      borderColor: "transparent #fff transparent transparent",
    },
  },
  headerText: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  bodyText: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  footerText: {
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    marginTop: theme.spacing(0.5),
  },
  mediaBox: {
    background: "#f0f0f0",
    borderRadius: 4,
    height: 130,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
    overflow: "hidden",
  },
  mediaImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  buttons: {
    marginTop: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
  },
  btn: {
    textTransform: "none",
    backgroundColor: "#fff",
    color: "#1976d2",
    border: "1px solid #e0e0e0",
    "&:hover": { backgroundColor: "#f7f7f7" },
  },
  empty: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.85rem",
  },
}));

function applyVarSubstitution(text, examples) {
  if (!text) return "";
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const v = examples?.[n];
    if (v && String(v).trim()) return String(v);
    return `[var ${n}]`;
  });
}

function ButtonIcon({ type }) {
  switch (type) {
    case "URL":
      return <LinkIcon fontSize="small" style={{ marginRight: 6 }} />;
    case "PHONE_NUMBER":
      return <PhoneIcon fontSize="small" style={{ marginRight: 6 }} />;
    case "COPY_CODE":
      return <FileCopyIcon fontSize="small" style={{ marginRight: 6 }} />;
    default:
      return <ReplyIcon fontSize="small" style={{ marginRight: 6 }} />;
  }
}

const TemplatePreview = ({ form, compact = false }) => {
  const classes = useStyles();

  const headerPreview = useMemo(() => {
    if (!form.header?.enabled) return null;
    if (form.header.format === "TEXT") {
      const text = applyVarSubstitution(form.header.text, form.header.examples);
      if (!text) return null;
      return (
        <Typography variant="body2" className={classes.headerText}>
          {text}
        </Typography>
      );
    }
    const url = form.header.media?.localUrl;
    if (form.header.format === "IMAGE") {
      return (
        <Box className={classes.mediaBox}>
          {url ? (
            <img src={url} alt="header" className={classes.mediaImg} />
          ) : (
            <Typography variant="caption" color="textSecondary">
              [imagem]
            </Typography>
          )}
        </Box>
      );
    }
    if (form.header.format === "VIDEO") {
      return (
        <Box className={classes.mediaBox}>
          {url ? (
            <video src={url} className={classes.mediaImg} controls={false} muted />
          ) : (
            <Typography variant="caption" color="textSecondary">
              [vídeo]
            </Typography>
          )}
        </Box>
      );
    }
    return (
      <Box className={classes.mediaBox}>
        <InsertDriveFileIcon style={{ fontSize: 48, color: "#999" }} />
      </Box>
    );
  }, [form.header, classes]);

  const bodyPreview = useMemo(() => {
    const text = applyVarSubstitution(form.body?.text || "", form.body?.examples);
    if (!text)
      return (
        <Typography className={classes.empty}>(corpo vazio)</Typography>
      );
    return (
      <Typography variant="body2" className={classes.bodyText}>
        {text}
      </Typography>
    );
  }, [form.body, classes]);

  const footerPreview =
    form.footer?.enabled && form.footer?.text ? (
      <Typography className={classes.footerText}>{form.footer.text}</Typography>
    ) : null;

  const buttonsPreview = useMemo(() => {
    if (!form.buttons?.enabled) return null;
    const items = Array.isArray(form.buttons.items) ? form.buttons.items : [];
    if (items.length === 0) return null;
    return (
      <Box className={classes.buttons}>
        {items.map((b, i) => (
          <Button
            key={i}
            size="small"
            variant="outlined"
            className={classes.btn}
            fullWidth
          >
            <ButtonIcon type={b.type} />
            {b.text || `Botão ${i + 1}`}
          </Button>
        ))}
      </Box>
    );
  }, [form.buttons, classes]);

  return (
    <Box
      className={classes.wrapper}
      style={
        compact
          ? { minHeight: 0, height: "auto", padding: 12, marginTop: 8 }
          : undefined
      }
    >
      <Box className={classes.bubble} style={compact ? { marginLeft: 0 } : undefined}>
        {headerPreview}
        {bodyPreview}
        {footerPreview}
        {buttonsPreview}
      </Box>
    </Box>
  );
};

export default TemplatePreview;

export { extractVarIndices };
